import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ProductsList from './ProductsList';
import ViewProduct from './ViewProduct';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import './Products.css';

function Products({ initialViewMode, initialProductId }) {
  const { t } = useTranslation('products');
  const [selectedProductId, setSelectedProductId] = useState(initialProductId || null);
  const [viewMode, setViewMode] = useState(initialViewMode || 'list'); // 'list', 'view', 'edit', 'create'

  useEffect(() => {
    // Set toolbar based on current mode
    if (window.setToolbar) {
      if (viewMode === 'list') {
        window.setToolbar({
          title: t('toolbar.titles.products'),
          buttons: [
            {
              label: t('toolbar.buttons.newProduct'),
              icon: '➕',
              onClick: handleNew,
              variant: 'primary'
            },
            {
              label: t('toolbar.buttons.refresh'),
              icon: '🔄',
              onClick: handleRefresh
            }
          ]
        });
      } else if (viewMode === 'view') {
        window.setToolbar({
          title: t('toolbar.titles.viewProduct'),
          showBack: true,
          onBack: handleBack,
          buttons: [
            {
              label: t('toolbar.buttons.edit'),
              icon: '✏️',
              onClick: handleEdit,
              variant: 'secondary'
            },
            {
              label: t('toolbar.buttons.delete'),
              icon: '🗑️',
              onClick: handleDelete,
              variant: 'danger'
            }
          ]
        });
      } else if (viewMode === 'edit') {
        window.setToolbar({
          title: t('toolbar.titles.editProduct'),
          showBack: false,
          buttons: [
            {
              label: t('toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('toolbar.buttons.save'),
              icon: '💾',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      } else if (viewMode === 'create') {
        window.setToolbar({
          title: t('toolbar.titles.newProduct'),
          showBack: false,
          buttons: [
            {
              label: t('toolbar.buttons.cancel'),
              onClick: handleCancel,
              variant: 'secondary'
            },
            {
              label: t('toolbar.buttons.create'),
              icon: '➕',
              onClick: handleSave,
              variant: 'primary'
            }
          ]
        });
      }
    }

    return () => {
      if (window.setToolbar) {
        window.setToolbar(null);
      }
    };
  }, [viewMode]);

  const handleSelectProduct = (productId) => {
    if (productId && productId !== 'undefined') {
      setSelectedProductId(productId);
      setViewMode('view');
    } else {
      console.error('Invalid product ID received:', productId);
    }
  };

  const handleNew = () => {
    setSelectedProductId(null);
    setViewMode('create');
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleDelete = () => {
    // Delete will be handled in ViewProduct component
    if (window.viewProductRef && window.viewProductRef.handleDelete) {
      window.viewProductRef.handleDelete();
    }
  };

  const handleSave = () => {
    // Save will be handled in ViewProduct component
    if (window.viewProductRef && window.viewProductRef.handleSave) {
      window.viewProductRef.handleSave();
    }
  };

  const handleCancel = () => {
    if (viewMode === 'create') {
      setViewMode('list');
      setSelectedProductId(null);
    } else {
      setViewMode('view');
    }
  };

  const handleBack = () => {
    setViewMode('list');
    setSelectedProductId(null);
  };

  const handleRefresh = () => {
    // Trigger refresh in ProductsList
    if (window.productsListRef && window.productsListRef.fetchProducts) {
      window.productsListRef.fetchProducts();
    }
  };

  const handleProductSaved = (product) => {
    setSelectedProductId(product.product_id);
    setViewMode('view');
    // Refresh the list
    if (window.productsListRef && window.productsListRef.fetchProducts) {
      window.productsListRef.fetchProducts();
    }
  };

  const handleProductCreated = (productId) => {
    setSelectedProductId(productId);
    setViewMode('view');
    // Refresh the list
    if (window.productsListRef && window.productsListRef.fetchProducts) {
      window.productsListRef.fetchProducts();
    }
  };

  return (
    <div className="products-page">
      <ResizablePanels
        leftPanel={
          <ProductsList
            ref={ref => window.productsListRef = ref}
            onSelectProduct={handleSelectProduct}
            selectedProductId={selectedProductId}
            onNew={handleNew}
          />
        }
        rightPanel={
          viewMode !== 'list' && (viewMode === 'create' || selectedProductId) ? (
            <ViewProduct
              ref={ref => window.viewProductRef = ref}
              productId={selectedProductId}
              onBack={handleBack}
              editMode={viewMode === 'edit'}
              createMode={viewMode === 'create'}
              onSave={handleProductSaved}
              onCancel={handleCancel}
              onEdit={handleEdit}
              onProductCreated={handleProductCreated}
            />
          ) : null
        }
        leftCollapsed={viewMode !== 'list'}
        defaultLeftWidth={40}
        minLeftWidth={30}
        maxLeftWidth={70}
      />
    </div>
  );
}

export default Products;